import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { Hint } from "@/components/Hint";
import { Link, useNavigate } from "react-router-dom";
import { ModeSwitch } from "@/components/Beginner";
import { AlertAttentionCard } from "@/components/AlertAttentionCard";
import { BillingSection } from "@/components/BillingSection";
import { PlanBadge } from "@/components/Locked";
import { DeleteAccountCard } from "@/components/DeleteAccountCard";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { useFeedback } from "@/context/FeedbackContext";
import { useInstallPrompt } from "@/lib/install";
import { Download } from "lucide-react";
import { MessageSquareHeart } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { plan } = useBilling();
  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Settings</h1>
        <p className="text-sm text-slate-400 mt-1">Manage your account, subscription and alert preferences.</p>
      </div>

      <div className="apx-card p-5" data-testid="account-card">
        <div className="font-display font-semibold mb-3">Account</div>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Field label="Name" value={user?.name} />
          <Field label="Email" value={user?.email} />
          <Field label="Plan" value={<PlanBadge plan={plan} />} />
          <Field label="Member since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"} />
        </div>
        <Hint k="logout" asChild>
        <button
          data-testid="logout-settings-btn"
          onClick={logout}
          className="btn-ghost rounded-lg px-3 py-2 text-sm mt-4"
        >
          Sign out
        </button>
        </Hint>
      </div>

      <BillingSection />

      <div className="apx-card p-5 flex items-center justify-between gap-4" data-testid="settings-mode-card">
        <div><div className="font-display font-semibold">Display mode</div><div className="text-xs text-slate-400 mt-0.5"><strong className="text-slate-300">Beginner</strong> adds plain-English explanations, examples and folds advanced tools behind "Show advanced details". <strong className="text-slate-300">Advanced</strong> shows every indicator at once. Switch any time — no feature is ever removed.</div></div>
        <ModeSwitch />
      </div>

      <div className="apx-card p-5 flex items-center justify-between gap-4" data-testid="settings-tour-card">
        <div><div className="font-display font-semibold">Guided tour & learning</div><div className="text-xs text-slate-400 mt-0.5">Replay the first-time walkthrough, or open Learn Crypto — short lessons with quizzes for every feature and term in EV-IDEX.</div></div>
        <div className="flex gap-2 shrink-0">
          <button data-testid="settings-replay-tour" onClick={() => { localStorage.removeItem("apx:tour-done"); navigate("/dashboard"); setTimeout(() => window.dispatchEvent(new Event("apx:start-tour")), 600); }} className="btn-ghost rounded-lg px-3 py-2 text-sm border border-slate-700">Replay tour</button>
          <Link to="/learn" data-testid="settings-open-learn" className="btn-primary rounded-lg px-4 py-2 text-sm font-medium">Learn Crypto</Link>
        </div>
      </div>

      <div className="apx-card p-5 flex items-center justify-between gap-4" data-testid="settings-notifications-link-card">
        <div><div className="font-display font-semibold">Notifications & alerts</div><div className="text-xs text-slate-400 mt-0.5">Phone push notifications, which alert types reach your phone, test notification, and your active / triggered alerts.</div></div>
        <Link to="/alerts?tab=notifications" data-testid="settings-open-notifications" className="btn-primary rounded-lg px-4 py-2 text-sm font-medium shrink-0">Open</Link>
      </div>

      <AlertAttentionCard />

      <InstallCard />

      <FeedbackCard />

      <ChangePasswordCard />
      <DeleteAccountCard />

      <div className="apx-card p-4 text-xs text-slate-400">
        <strong className="text-amber-300">Risk disclosure:</strong> EV-IDEX is for educational and research purposes only. All trading in this app is simulated with virtual funds. Cryptocurrency markets are highly volatile; past AI signals do not guarantee future results. Nothing on this platform constitutes financial advice. Always do your own research and consider consulting a qualified financial professional before making investment decisions.
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1"><Link to="/terms" className="text-emerald-400 hover:underline" data-testid="settings-terms-link">Terms of Service</Link><Link to="/privacy" className="text-emerald-400 hover:underline" data-testid="settings-privacy-link">Privacy Policy</Link><Link to="/risk-disclosure" className="text-emerald-400 hover:underline" data-testid="settings-risk-link">Risk Disclosure</Link><Link to="/contact" className="text-emerald-400 hover:underline" data-testid="settings-contact-link">Contact & Support</Link><Link to="/partners" className="text-emerald-400 hover:underline" data-testid="settings-partners-link">Investment Partners</Link><Link to="/delete-account" className="text-emerald-400 hover:underline" data-testid="settings-delete-info-link">Account deletion</Link></div>
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{label}</div>
    <div className="mt-0.5 font-medium">{value ?? "—"}</div>
  </div>
);

const FeedbackCard = () => {
  const { openFeedback } = useFeedback();
  return (
    <div className="apx-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="settings-feedback-card">
      <div>
        <div className="font-display font-semibold">Give Feedback</div>
        <div className="text-xs text-slate-400 mt-0.5">Tell us what you like, what to improve and what to build next. Every submission reaches the EV-IDEX team and is tracked until resolved.</div>
      </div>
      <button type="button" onClick={() => openFeedback({ source: "settings" })} data-testid="settings-feedback-btn" className="btn-primary rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2 shrink-0"><MessageSquareHeart className="w-4 h-4" /> Give Feedback</button>
    </div>
  );
};

const InstallCard = () => {
  const { canPrompt, prompt, standalone, platform } = useInstallPrompt();
  const navigate = useNavigate();
  if (standalone) return null;
  const go = async () => { if (canPrompt) { await prompt(); } else navigate("/install"); };
  return (
    <div className="apx-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="settings-install-card">
      <div className="flex items-center gap-3">
        <img src="/icons/icon-192.png" alt="" className="w-11 h-11 rounded-xl" />
        <div>
          <div className="font-display font-semibold">Install the EV-IDEX app</div>
          <div className="text-xs text-slate-400 mt-0.5">{platform === "ios" ? "Add EV-IDEX to your iPhone home screen from Safari — full-screen app with push alerts." : "Get the EV-IDEX icon on your home screen — full-screen app, push alerts, automatic updates."}</div>
        </div>
      </div>
      <button type="button" onClick={go} data-testid="settings-install-btn" className="btn-primary rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2 shrink-0"><Download className="w-4 h-4" /> {canPrompt ? "Install now" : "How to install"}</button>
    </div>
  );
};
